import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/axios';
import { T, PageHeader, Section, Field, Alert, Row } from '../components/UI';

export default function JobEdit() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const { user, can }  = useAuth();

  const [job,         setJob]         = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [saving,      setSaving]      = useState(false);
  const [error,       setError]       = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [saved,       setSaved]       = useState(false);
  const [services,    setServices]    = useState([]);
  const [locations,   setLocations]   = useState([]);
  const [dealerships, setDealerships] = useState([]);
  const [poDupWarnings,  setPoDupWarnings]  = useState({});
  const [poDupConfirmed, setPoDupConfirmed] = useState({});

  const [form, setForm] = useState({
    location:'', dealership:'', stockNum:'', vinNum:'', rego:'',
    description:'', coreServices:[], extraNotes:'', delTo:'',
    poEntries:[{ poNumber:'', price:'', services:[] }],
    invoiceNumber:'',
  });

  useEffect(() => {
    api.get('/jobs/services').then(({data})=>setServices((data.services||[]).slice().sort((a,b)=>a.name.localeCompare(b.name)))).catch(()=>{});
    api.get('/jobs/locations').then(({data})=>setLocations(data.locations)).catch(()=>{});
    api.get('/jobs/dealerships').then(({data})=>setDealerships(data.dealerships)).catch(()=>{});
  }, []);

  useEffect(() => {
    api.get(`/jobs/${id}`)
      .then(({data}) => {
        const j = data.job;
        setJob(j);
        setForm({
          location:      j.location      || '',
          dealership:    j.dealership    || '',
          stockNum:      j.stockNum      || '',
          vinNum:        j.vinNum        || '',
          rego:          j.rego          || '',
          description:   j.description   || '',
          coreServices:  j.coreServices  || [],
          extraNotes:    (j.poEntries && j.poEntries[0]?.extraNotes) || '',
          delTo:         j.delTo         || '',
          poEntries:     j.poEntries && j.poEntries.length > 0
                           ? j.poEntries.map((e,i)=>({ poNumber:e.poNumber||'', price:e.price||'', services:(e.services&&e.services.length>0)?e.services:(i===0?(j.coreServices||[]):[]), invoiceNumber:e.invoiceNumber||'', extraNotes:e.extraNotes||'' }))
                           : [{ poNumber:j.poNumber||'', price:j.price||'', services:j.coreServices||[], invoiceNumber:j.invoiceNumber||'' }],
        });
      })
      .catch(err => setError(err.response?.data?.message || 'Failed to load job'))
      .finally(() => setLoading(false));
  }, [id]);

  const toggleService = (svc) => setForm(f=>({ ...f, coreServices:f.coreServices.includes(svc)?f.coreServices.filter(s=>s!==svc):[...f.coreServices,svc] }));

  const checkPODuplicate = async (poNumber, entryIdx) => {
    if (!poNumber || poNumber.length < 3) { setPoDupWarnings(w=>({...w,[entryIdx]:null})); return; }
    try {
      const { data } = await api.get('/jobs/check-po', { params:{ poNumber:poNumber.toUpperCase(), excludeId:id, date:job?.createdAt?.split('T')[0]||new Date(new Date().getTime() + 8*60*60*1000).toISOString().split('T')[0] } });
      setPoDupWarnings(w=>({...w,[entryIdx]:data.found?data:null}));
      if (data.found) setPoDupConfirmed(p=>({...p,[entryIdx]:false}));
    } catch {}
  };

  const handleSave = async () => {
    const unconfirmed = form.poEntries.some((_,idx)=>poDupWarnings[idx]&&!poDupConfirmed[idx]);
    if (unconfirmed) { setError('Please confirm or clear duplicate PO numbers before saving.'); return; }
    const newFieldErrors = {};
    if (form.coreServices.includes('Others') && !form.extraNotes.trim()) {
      newFieldErrors.extraNotesTop = true;
    }
    form.poEntries.forEach((entry, idx) => {
      if (idx === 0) return;
      if ((entry.services||[]).includes('Others') && !(entry.extraNotes||'').trim()) {
        newFieldErrors[`extraNotes_${idx}`] = true;
      }
    });
    if (Object.keys(newFieldErrors).length > 0) {
      setFieldErrors(newFieldErrors);
      setError('Extra Notes is required where "Others" service is selected.');
      return;
    }
    setSaving(true); setError(null);
    try {
      const update = {
        location:     form.location,
        dealership:   form.dealership,
        stockNum:     form.stockNum.toUpperCase().trim(),
        vinNum:       form.vinNum.toUpperCase().trim(),
        rego:         form.rego.toUpperCase().trim(),
        description:  form.description,
        coreServices: form.coreServices,
        extraNotes:   [form.extraNotes?.trim() ? (form.poEntries.length>1?'PO1: ':'')+form.extraNotes.trim() : '', ...form.poEntries.slice(1).map((e,i)=>e.extraNotes?.trim() ? `PO${i+2}: `+e.extraNotes.trim() : '')].filter(Boolean).join(' | '),
        delTo:        form.delTo,
        poEntries:    form.poEntries.map((e,i)=>({ poNumber:e.poNumber.toUpperCase().trim(), price:parseFloat(e.price)||0, services:i===0?form.coreServices:(e.services||[]), invoiceNumber:(e.invoiceNumber||'').toUpperCase().trim(), extraNotes:i===0?(form.extraNotes||''):(e.extraNotes||'') })),
        price:        parseFloat(form.poEntries[0]?.price)||0,
        poNumber:     form.poEntries[0]?.poNumber?.toUpperCase().trim()||'',
        hasPO:        form.poEntries.some(e=>e.poNumber?.trim()),
        coreServices: form.coreServices,
        invoiceNumber: (form.poEntries.find(e=>e.invoiceNumber)?.invoiceNumber||'').toUpperCase().trim()||undefined,
      };
      if (update.hasPO) update.status='po_received';
      if (update.invoiceNumber) update.status='invoiced';
      await api.patch(`/jobs/${id}`, update);
      setSaved(true);
    } catch(err) { setError(err.response?.data?.message||'Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <div style={{ minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center', background:'var(--bg)', color:'var(--muted)', fontFamily:'Inter,sans-serif' }}>
      <i className="ti ti-loader" style={{ fontSize:'24px', color:'var(--blue)' }} aria-hidden="true"/>
    </div>
  );

  if (saved) return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center', fontFamily:'Inter,sans-serif' }}>
      <div style={{ textAlign:'center', maxWidth:'360px', padding:'20px' }}>
        <div style={{ width:'56px', height:'56px', borderRadius:'50%', background:'var(--green-bg)', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
          <i className="ti ti-check" style={{ fontSize:'28px', color:'var(--green)' }} aria-hidden="true"/>
        </div>
        <h2 style={{ fontSize:'18px', fontWeight:'500', color:'var(--text)', marginBottom:'8px' }}>Changes Saved</h2>
        <button onClick={()=>navigate(-1)} style={{ ...T.btnPrimary, margin:'0 auto' }}>Go Back</button>
      </div>
    </div>
  );

  return (
    <div style={T.page}>
      <PageHeader title={`Edit Job${job?.stockNum ? ` — ${job.stockNum}` : ''}`} onBack={()=>navigate(-1)}
        actions={
          <span style={{ fontSize:'11px', color:'var(--muted)', fontFamily:'JetBrains Mono,monospace' }}>
            {job?.createdAt && new Date(job.createdAt).toLocaleDateString('en-AU')}
          </span>
        }
      />

      <div style={{ maxWidth:'680px', margin:'0 auto', padding:'20px 16px 100px' }}>
        {error && <Alert type="error">{error}</Alert>}

        {/* Location banner */}
        <div style={{ background:'var(--blue-light)', border:'1px solid rgba(10,110,180,0.2)', borderRadius:'var(--radius)', padding:'10px 14px', marginBottom:'12px', display:'flex', alignItems:'center', gap:'8px' }}>
          <i className="ti ti-map-pin" style={{ color:'var(--blue)', fontSize:'16px', flexShrink:0 }} aria-hidden="true"/>
          <span style={{ fontSize:'13px', color:'var(--blue)', fontWeight:'500' }}>{form.location || 'No location set'}</span>
          <span style={{ fontSize:'12px', color:'var(--muted)', marginLeft:'auto' }}>Job location</span>
        </div>

        {/* Dealership tiles */}
        <Section title="Select Dealership *" accent>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))', gap:'8px' }}>
            {dealerships.map(d=>{
              const sel = form.dealership === d.name;
              return (
                <button key={d._id} type="button"
                  onClick={()=>setForm(f=>({...f,dealership:d.name}))}
                  style={{ padding:'12px 10px', borderRadius:'var(--radius)', border:`2px solid ${sel?'var(--blue)':'var(--border-md)'}`, background:sel?'var(--blue)':'var(--surface)', color:sel?'#fff':'var(--text)', fontSize:'13px', fontWeight:sel?'600':'400', cursor:'pointer', textAlign:'center', fontFamily:'inherit', transition:'all .15s', display:'flex', flexDirection:'column', alignItems:'center', gap:'6px' }}>
                  <i className="ti ti-building" style={{ fontSize:'20px', opacity:sel?1:0.4 }} aria-hidden="true"/>
                  {d.name}
                </button>
              );
            })}
          </div>
        </Section>

        {/* PO Entries & Pricing — right after dealership */}
        <Section title="PO Entries & Pricing" accent>
          {form.poEntries.map((entry,idx)=>(
            <div key={idx} style={{ background:'var(--bg)', border:'1px solid var(--border)', borderRadius:'var(--radius)', padding:'14px', marginBottom:'10px', position:'relative' }}>
              {form.poEntries.length>1 && (
                <div style={{ fontSize:'12px', fontWeight:'500', color:'var(--muted)', textTransform:'uppercase', letterSpacing:'.5px', marginBottom:'10px' }}>PO Entry {idx+1}</div>
              )}
              <Row cols={3}>
                <Field label="PO Number">
                  <input value={entry.poNumber}
                    onChange={e=>{const entries=[...form.poEntries]; entries[idx]={...entries[idx],poNumber:e.target.value.toUpperCase()}; setForm(f=>({...f,poEntries:entries})); checkPODuplicate(e.target.value,idx);}}
                    style={T.inputMono} placeholder="PO number"/>
                </Field>
                <Field label="Invoice Number">
                  <input value={entry.invoiceNumber||''}
                    onChange={e=>{const entries=[...form.poEntries]; entries[idx]={...entries[idx],invoiceNumber:e.target.value.toUpperCase()}; setForm(f=>({...f,poEntries:entries}));}}
                    style={{ ...T.inputMono, borderColor:'rgba(83,74,183,0.3)' }} placeholder="Invoice #"/>
                </Field>
                <Field label="Price ($)">
                  <div style={{ position:'relative' }}>
                    <span style={{ position:'absolute', left:'12px', top:'50%', transform:'translateY(-50%)', color:'var(--muted)' }}>$</span>
                    <input type="number" value={entry.price}
                      onChange={e=>{const entries=[...form.poEntries]; entries[idx]={...entries[idx],price:e.target.value}; setForm(f=>({...f,poEntries:entries}));}}
                      style={{ ...T.inputMono, paddingLeft:'24px' }} placeholder="0.00" step="0.01" min="0"/>
                  </div>
                </Field>
              </Row>

              {poDupWarnings[idx] && !poDupConfirmed[idx] && (
                <Alert type="error">
                  PO <strong>{entry.poNumber}</strong> used on a different date.
                  <div style={{ marginTop:'8px', display:'flex', gap:'8px' }}>
                    <button type="button" onClick={()=>setPoDupConfirmed(p=>({...p,[idx]:true}))} style={{ ...T.btnPrimary, fontSize:'12px', padding:'5px 12px', background:'var(--red)' }}>Proceed Anyway</button>
                    <button type="button" onClick={()=>{const entries=[...form.poEntries]; entries[idx]={...entries[idx],poNumber:''}; setForm(f=>({...f,poEntries:entries})); setPoDupWarnings(w=>({...w,[idx]:null}));}} style={{ ...T.btnSecondary, fontSize:'12px', padding:'5px 12px' }}>Clear PO</button>
                  </div>
                </Alert>
              )}

              {idx>0 && (
                <div style={{ marginTop:'10px' }}>
                  <label style={T.label}>Services for PO {idx+1}</label>
                  <div style={{ display:'flex', flexWrap:'wrap', gap:'6px', marginTop:'6px' }}>
                    {services.map(svc=>{
                      const sel=(entry.services||[]).includes(svc.name);
                      const blocked = svc.name !== 'Others' && (form.poEntries.some((e,i)=>i!==idx&&(e.services||[]).includes(svc.name))||form.coreServices.includes(svc.name));
                      return (
                        <button key={svc._id} type="button" disabled={blocked}
                          onClick={()=>{ if(blocked)return; const entries=[...form.poEntries]; const svcs=entries[idx].services||[]; entries[idx]={...entries[idx],services:sel?svcs.filter(s=>s!==svc.name):[...svcs,svc.name]}; setForm(f=>({...f,poEntries:entries})); }}
                          style={{ padding:'5px 12px', borderRadius:'20px', border:`1px solid ${blocked?'var(--border)':sel?'var(--blue)':'var(--border-md)'}`, background:blocked?'var(--bg)':sel?'var(--blue-light)':'var(--surface)', color:blocked?'var(--hint)':sel?'var(--blue)':'var(--muted)', fontSize:'12px', cursor:blocked?'not-allowed':'pointer', fontFamily:'inherit' }}>
                          {svc.name}{blocked?' (used)':''}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
              {idx===0 && <p style={{ fontSize:'11px', color:'var(--muted)', marginTop:'8px' }}>Uses general services selected above</p>}

              {/* Per-PO Extra Notes — only for PO2+ when Others is selected */}
              {idx>0 && (entry.services||[]).includes('Others') && (
                <div style={{ marginTop:'12px' }}>
                  <Field label="Extra Notes *" error={fieldErrors[`extraNotes_${idx}`] ? 'Required when "Others" is selected' : null}>
                    <textarea value={entry.extraNotes||''}
                      onChange={e=>{const entries=[...form.poEntries]; entries[idx]={...entries[idx],extraNotes:e.target.value}; setForm(f=>({...f,poEntries:entries})); setFieldErrors(fe=>({...fe,[`extraNotes_${idx}`]:false}));}}
                      rows={2} style={{ ...T.input, resize:'vertical', borderColor:fieldErrors[`extraNotes_${idx}`]?'var(--red)':'var(--border-md)' }}
                      placeholder="Describe the 'Others' service for this PO..."/>
                  </Field>
                </div>
              )}

              {form.poEntries.length>1 && (
                <button type="button" onClick={()=>setForm(f=>({...f,poEntries:f.poEntries.filter((_,i)=>i!==idx)}))}
                  style={{ position:'absolute', top:'10px', right:'10px', ...T.btnIcon, color:'var(--red)', borderColor:'rgba(163,45,45,0.25)', fontSize:'12px' }}>
                  Remove
                </button>
              )}
            </div>
          ))}
          <button type="button" onClick={()=>setForm(f=>({...f,poEntries:[...f.poEntries,{poNumber:'',price:'',services:[]}]}))}
            style={{ width:'100%', background:'transparent', border:'1px dashed var(--border-md)', borderRadius:'var(--radius)', padding:'10px', color:'var(--blue)', fontSize:'13px', cursor:'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
            <i className="ti ti-plus" aria-hidden="true"/> Add Another PO / Price
          </button>
        </Section>

        {/* Vehicle — Stock/VIN/Rego in one row */}
        <Section title="Vehicle Details" accent>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:'10px' }}>
            <Field label="Stock #">
              <input value={form.stockNum} onChange={e=>setForm(f=>({...f,stockNum:e.target.value.toUpperCase()}))} style={T.inputMono} placeholder="40383C"/>
            </Field>
            <Field label="VIN">
              <input value={form.vinNum} onChange={e=>setForm(f=>({...f,vinNum:e.target.value.toUpperCase()}))} style={T.inputMono} placeholder="VIN number"/>
            </Field>
            <Field label="Rego">
              <input value={form.rego} onChange={e=>setForm(f=>({...f,rego:e.target.value.toUpperCase()}))} style={T.inputMono} placeholder="Optional"/>
            </Field>
          </div>
        </Section>

        {/* Description */}
        <Section title="Description" accent>
          <Field label="Description">
            <input value={form.description} onChange={e=>setForm(f=>({...f,description:e.target.value}))} style={T.input} placeholder="e.g. 2024 Hyundai Tucson"/>
          </Field>
        </Section>

        {/* Services — icon chips */}
        <Section title="General Services (PO 1)" accent>
          <div style={{ display:'flex', flexWrap:'wrap', gap:'8px' }}>
            {services.map(svc=>{
              const sel=form.coreServices.includes(svc.name);
              return (
                <button key={svc._id} type="button" onClick={()=>toggleService(svc.name)}
                  style={{ display:'inline-flex', alignItems:'center', gap:'5px', padding:'7px 14px', borderRadius:'20px', border:`2px solid ${sel?'var(--blue)':'var(--border-md)'}`, background:sel?'var(--blue)':'var(--surface)', color:sel?'#fff':'var(--muted)', fontSize:'13px', fontWeight:sel?'600':'400', cursor:'pointer', fontFamily:'inherit', transition:'all .15s', whiteSpace:'nowrap' }}>
                  {sel && <i className="ti ti-check" style={{ fontSize:'12px' }} aria-hidden="true"/>}
                  {svc.name}
                </button>
              );
            })}
          </div>
        </Section>

        {/* Extra details */}
        <Section title="Extra Details" accent>
          <Field label={form.coreServices.includes('Others') && fieldErrors.extraNotesTop ? 'Extra Notes *' : 'Extra Notes'} error={form.coreServices.includes('Others') && fieldErrors.extraNotesTop ? 'Required when "Others" is selected' : null}>
            <textarea value={form.extraNotes} onChange={e=>{setForm(f=>({...f,extraNotes:e.target.value})); setFieldErrors(fe=>({...fe,extraNotesTop:false}));}}
              rows={2} style={{ ...T.input, resize:'vertical', borderColor:(form.coreServices.includes('Others') && fieldErrors.extraNotesTop)?'var(--red)':'var(--border-md)' }} placeholder="Additional notes"/>
          </Field>
          <Field label="Delivered To">
            <input value={form.delTo} onChange={e=>setForm(f=>({...f,delTo:e.target.value}))} style={T.input} placeholder="e.g. Front lot"/>
          </Field>
        </Section>

        {/* Edit history — owner only */}
        {can('canDeleteJobs') && job?.editHistory?.length>0 && (
          <Section title="Edit History">
            {job.editHistory.slice().reverse().map((h,i)=>(
              <div key={i} style={{ display:'flex', gap:'10px', padding:'8px 0', borderBottom:'1px solid var(--border)', fontSize:'12px' }}>
                <span style={{ color:'var(--muted)', fontFamily:'JetBrains Mono,monospace', whiteSpace:'nowrap' }}>{new Date(h.editedAt).toLocaleDateString('en-AU')}</span>
                <span style={{ color:'var(--blue)', fontWeight:'500' }}>{h.editedByName}</span>
                <span style={{ color:'var(--muted)', flex:1 }}>
                  {Object.entries(h.changes||{}).map(([k,{from,to}])=>(
                    <span key={k}>{k}: <span style={{ color:'var(--red)' }}>{String(from).substring(0,20)}</span> → <span style={{ color:'var(--green)' }}>{String(to).substring(0,20)}</span> </span>
                  ))}
                  {h.note && <span style={{ color:'var(--hint)' }}>({h.note})</span>}
                </span>
              </div>
            ))}
          </Section>
        )}

        <div style={{ display:'flex', gap:'10px', marginTop:'8px' }}>
          <button onClick={()=>navigate(-1)} style={{ ...T.btnSecondary, flex:1 }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ ...T.btnPrimary, flex:2, justifyContent:'center', opacity:saving?0.6:1 }}>
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
}
